import { type B2bOrderDetail, getB2bOrderFileUrl, PAYMENT_REJECTION_REASONS, type PaymentRecord, rejectB2bPayment, verifyB2bPayment } from '@api/b2b';
import { DataSheet, type DataSheetColumn } from '@components/ui/DataSheet';
import { Button, Modal, Select, Textarea } from '@mantine/core';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import React, { useState } from 'react';
import styles from './b2b.module.css';
import { Field, Pill } from './shared';
import { errorMessage, formatDay, formatMoment, money, openSignedFile } from './utils';


/** Accounts' side of the advance: each proof the customer uploaded, verified or rejected here. */
const PaymentsTab: React.FC<{ detail: B2bOrderDetail; onChanged: () => void }> = ({ detail, onChanged }) => {
    const { order, payments } = detail;
    const [busy, setBusy] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [rejecting, setRejecting] = useState<PaymentRecord | null>(null);
    const [reason, setReason] = useState<string | null>(null);
    const [note, setNote] = useState('');

    const run = async (id: string, action: () => Promise<unknown>) => {
        setBusy(id);
        setError(null);
        try {
            await action();
            onChanged();
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setBusy(null);
        }
    };

    const columns: DataSheetColumn<PaymentRecord>[] = [
        { key: 'paidOn', header: 'Paid on', width: 110, render: (v: string) => formatDay(v) },
        { key: 'amount', header: 'Amount', width: 120, align: 'right', render: (v: number) => money(v) },
        { key: 'method', header: 'Method', width: 90 },
        {
            key: 'reference',
            header: 'UTR / reference',
            width: 200,
            render: (v: string, r) => (
                <div className={styles.stack}>
                    <span>{v}</span>
                    {r.duplicateOf.length ? <span className={styles.error}>Also on {r.duplicateOf.join(', ')}</span> : null}
                </div>
            ),
        },
        {
            key: 'status',
            header: 'Status',
            width: 170,
            render: (v: string | undefined, r) => (
                <div className={styles.stack}>
                    <Pill label={v ?? 'VERIFIED'} />
                    {v === 'SUBMITTED' && r.verifyBy ? (
                        <span className={r.overdue ? styles.error : styles.muted}>
                            {r.overdue ? 'Overdue since' : 'Verify by'} {formatMoment(r.verifyBy)}
                        </span>
                    ) : null}
                    {v === 'REJECTED' ? <span className={styles.muted}>{PAYMENT_REJECTION_REASONS.find((x) => x.value === r.rejectionReason)?.label ?? r.rejectionReason}</span> : null}
                </div>
            ),
        },
        {
            key: 'proofKeys',
            header: 'Proof',
            width: 90,
            render: (keys: string[] | undefined) =>
                keys?.length ? (
                    <button type="button" className={styles.textLink} onClick={() => void openSignedFile(() => getB2bOrderFileUrl(order.purchaseOrderId, keys[0]!))}>
                        View
                    </button>
                ) : (
                    '—'
                ),
        },
    ];
    // Verify / Reject only while a proof is waiting; no empty column otherwise.
    if (payments.payments.some((p) => p.status === 'SUBMITTED')) {
        columns.push({
            key: 'id',
            header: 'Action',
            width: 190,
            render: (_v, r) =>
                r.status === 'SUBMITTED' ? (
                    <div className={styles.row}>
                        <Button size="xs" variant={ButtonVariant.ACTION} loading={busy === r.id} onClick={() => void run(r.id, () => verifyB2bPayment(order.purchaseOrderId, r.id))}>
                            Verify
                        </Button>
                        <Button size="xs" variant={ButtonVariant.OUTLINED_DESTRUCTIVE} onClick={() => setRejecting(r)}>
                            Reject
                        </Button>
                    </div>
                ) : null,
        });
    }

    return (
        <div className={styles.panelBody}>
            {/* Row 1 is the money (payable - verified = outstanding); row 2 what gates dispatch. */}
            <div className={styles.fieldGrid3}>
                <Field label="Payable" value={money(payments.payable)} />
                <Field label="Verified" value={money(payments.settled)} />
                <Field label="Outstanding" value={money(payments.outstanding)} />
                <Field label="Due before dispatch" value={money(payments.dueBeforeDispatch)} />
                <Field
                    label="Waiting for verification"
                    value={payments.pendingVerification === 0 ? 'None' : `${payments.pendingVerification} payment${payments.pendingVerification === 1 ? '' : 's'}`}
                />
            </div>
            <DataSheet<PaymentRecord>
                bare
                hideHeaderBar
                data={payments.payments}
                columns={columns}
                getRowKey={(r) => r.id}
                maxHeight="auto"
                emptyState="No payment has been uploaded yet. The customer uploads proof after confirming the PI."
            />
            {error ? <span className={styles.error}>{error}</span> : null}

            <Modal opened={!!rejecting} onClose={() => setRejecting(null)} title="Reject payment" centered>
                <div className={styles.stack}>
                    <Select label="Reason" data={PAYMENT_REJECTION_REASONS} value={reason} onChange={setReason} />
                    <Textarea label="Note to the customer" autosize minRows={2} value={note} onChange={(e) => setNote(e.currentTarget.value)} />
                    <Button
                        variant={ButtonVariant.FILLED_DESTRUCTIVE}
                        disabled={!reason}
                        loading={!!rejecting && busy === rejecting.id}
                        onClick={() => {
                            if (!rejecting || !reason) return;
                            const target = rejecting;
                            void run(target.id, () => rejectB2bPayment(order.purchaseOrderId, target.id, reason, note.trim() || undefined)).then(() => {
                                setRejecting(null);
                                setReason(null);
                                setNote('');
                            });
                        }}
                    >
                        Reject payment
                    </Button>
                </div>
            </Modal>
        </div>
    );
};

export default PaymentsTab;
